import {
  Button,
  HStack,
  Spacer,
  Table,
  Stack,
  useDisclosure,
  IconButton,
  Text,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import { useMemo, useState } from "react";
import { TreatmentType } from "@keita/lanepatrol-sdk";
import { ForecastActionType } from "../../../../../../project/models/ForecastScenario";
import { OsaNode } from "../../../../../../osaway/models/OsaNode";
import { getTreatmentLabel } from "../../../../../../project/utils/treatment-labels";
import { StmModal } from "../../../../../../shared/views/StmModal/StmModal";
import { useStores } from "../../../../../../shared/stores/RootStore";
import { InfoOutlineIcon } from "../../../../../../shared/views/icons";
import { formatDistanceByUnit } from "../../../../../../shared/utils/format-utils";

interface Props {
  filteredDistance: number;
  filteredNodes: OsaNode[];
  onClose: () => void;
}
const AddHighestPriorityTreatmentActionBase: React.FC<Props> = ({
  filteredDistance,
  filteredNodes,
  onClose,
}) => {
  const { forecastStore, projectStore } = useStores();
  const [autoFocusTreatmentType, setAutoFocusTreatmentType] =
    useState<TreatmentType>();
  const stmModal = useDisclosure();
  const tableData = useMemo(() => {
    const counterMap = new Map<TreatmentType | null, number>();

    for (const node of filteredNodes) {
      const [firstTreatment] = node.treatments!;
      const counterKey = firstTreatment ? firstTreatment.type : null;
      const counter = counterMap.get(counterKey) ?? 0;
      counterMap.set(counterKey, counter + 1);
    }

    return [...counterMap.entries()].sort((a, b) => b[1] - a[1]);
  }, [filteredNodes]);

  const onSubmit = () => {
    forecastStore.addChange({
      type: ForecastActionType.HighestPriorityTreatment,
    });
    onClose();
  };

  return (
    <>
      <Stack gap={4}>
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>
                {t(`${localPrefix}.table.header.treatment`)}
              </Table.ColumnHeader>
              <Table.ColumnHeader textAlign="end">
                {t(`${localPrefix}.table.header.counter`)}
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {tableData.map(([treatmentType, counter]) => (
              <Table.Row key={treatmentType}>
                <Table.Cell fontSize="sm">
                  <HStack>
                    <Text>
                      {treatmentType
                        ? getTreatmentLabel(treatmentType)
                        : t(`${localPrefix}.table.nullTreatment`)}
                    </Text>
                    {treatmentType !== null && (
                      <IconButton
                        my={-2}
                        variant="ghost"
                        colorPalette="primary"
                        size="sm"
                        onClick={() => {
                          setAutoFocusTreatmentType(treatmentType);
                          stmModal.onOpen();
                        }}
                      >
                        <InfoOutlineIcon />
                      </IconButton>
                    )}
                  </HStack>
                </Table.Cell>
                <Table.Cell textAlign="end">{counter}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
        <HStack>
          <Spacer />
          <Button
            flexShrink={0}
            size="sm"
            colorPalette="primary"
            onClick={onSubmit}
            disabled={!filteredNodes.length}
          >
            {t(`map:forecast.addActionModal.applyBtn`, {
              count: filteredNodes.length,
              total: formatDistanceByUnit(
                filteredDistance,
                projectStore.distanceUnit,
              ),
              unit: projectStore.distanceUnit,
            })}
          </Button>
        </HStack>
      </Stack>

      {stmModal.open && (
        <StmModal
          onClose={stmModal.onClose}
          autoFocusTreatmentType={autoFocusTreatmentType}
        />
      )}
    </>
  );
};

export const AddHighestPriorityTreatmentAction = observer(
  AddHighestPriorityTreatmentActionBase,
);

const localPrefix = "map:forecast.HighestPriorityTreatment";
